import React, { useCallback, useState, useRef, useEffect } from 'react';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import {
  Main,
  ModalBlock,
  ModalHeader,
  ModalTitle,
  Icon,
  ModalContent,
  ModalForm,
  Label,
  ModalButton,
  ModalInput,
  InputDiv,
  InputData,
  SelectedUser,
  Input,
  Dropdiv
} from './styles';
import Button from '../../button';
import { useSelector } from 'react-redux';
import { RootReducerInterface } from '../../../interfaces/RootReducerInterface';
import { UsersWorkspaceInterface } from '../../../interfaces/WorkspaceInterface';
import { UserInterface } from '../../../interfaces/chatMessageInterface';
import { Dropdown, DropdownItem } from '../../Dropdown';
import { useMobileDevice } from '../../../global/useMobile';

interface Props {
  onClose?: () => void;
  heading?: string;
  label?: string;
  isEditGroupModal?: boolean;
  usersList?: UsersWorkspaceInterface[];
  onChange?: (propsName: string, value: string) => void;
  groupUserList?: UserInterface[];
  onAddMembers?: (e: React.SyntheticEvent, users: string[]) => void;
  loading?: boolean;
  onEditGroupName?: (e: React.SyntheticEvent) => void;
}

const Addgroupmembermodal: React.FC<Props> = (props) => {
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const isMobile = useMobileDevice();
  const isMinWidth = isMobile ? Math.max(window.innerWidth - 65) : 368;

  //use selector state variables
  const stateSelector = useSelector((state: RootReducerInterface) => state);
  const { chatMessage: chatMessageSelector } = stateSelector || {};
  const { inputChatGroup } = chatMessageSelector || {};

  //Other variable
  const existUsersIds = props?.groupUserList?.map((user) => user?.id);
  const filteredUsersList = props?.usersList?.filter((item) => !existUsersIds?.includes(item?.id));

  const handleSelectChangeUsers = useCallback(
    (userId: string) => {
      if (!selectedUsers.includes(userId)) {
        setSelectedUsers([...selectedUsers, userId]);
        setSearchTerm('');
      }
    },
    [selectedUsers]
  );

  const handleRemoveUser = useCallback(
    (userId: string) => {
      setSelectedUsers(selectedUsers.filter((id) => id !== userId));
    },
    [selectedUsers]
  );

  const scrollToItem = useCallback((index: number) => {
    const item = itemRefs.current[index];
    if (item && dropdownRef.current) {
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const itemHeight = itemRect.height;

      // Calculate the position to scroll to
      const scrollTop = dropdownRef.current.scrollTop;
      const itemTop = itemRect.top - dropdownRect.top + scrollTop;
      const itemBottom = itemTop + itemHeight;
      const dropdownHeight = dropdownRect.height;

      // If item is below the visible area
      if (itemBottom > dropdownHeight + scrollTop) {
        dropdownRef.current.scrollTop = itemBottom - dropdownHeight;
      }
      // If item is above the visible area
      else if (itemTop < scrollTop) {
        dropdownRef.current.scrollTop = itemTop;
      }
    }
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const filteredUsers =
        filteredUsersList?.filter(
          (user) =>
            user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchTerm.toLowerCase())
        ) || [];

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          setCurrentIndex((prevIndex) => {
            const newIndex = prevIndex === 0 ? filteredUsers.length - 1 : prevIndex - 1;
            scrollToItem(newIndex);
            return newIndex;
          });
          break;
        case 'ArrowDown':
          e.preventDefault();
          setCurrentIndex((prevIndex) => {
            const newIndex = prevIndex === filteredUsers.length - 1 ? 0 : prevIndex + 1;
            scrollToItem(newIndex);
            return newIndex;
          });
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredUsers[currentIndex]) {
            handleSelectChangeUsers(filteredUsers[currentIndex].id);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsDropdownOpen(false);
          break;
        case 'Backspace':
          if (searchTerm === '' && selectedUsers.length > 0) {
            setSelectedUsers(selectedUsers.slice(0, -1));
          }
          break;
        default:
          break;
      }
    },
    [currentIndex, filteredUsersList, searchTerm, selectedUsers, handleSelectChangeUsers, scrollToItem]
  );

  useEffect(() => {
    if (isDropdownOpen) {
      setCurrentIndex(0);
      // Reset item refs array
      itemRefs.current = [];
      // Focus the dropdown
      dropdownRef.current?.focus();
    }
  }, [isDropdownOpen]);

  const handleToggleDropdown = useCallback(() => {
    setIsDropdownOpen((prev) => !prev);
  }, []);

  return (
    <div>
      <Main>
        <ModalBlock>
          <ModalHeader>
            <ModalTitle>{props?.heading}</ModalTitle>
          </ModalHeader>
          <Icon onClick={props?.onClose}>
            <SVGIcon name='modal-cross-sign-icon' width='18' height='18' viewBox='0 0 18 18' className='fillColor' />
          </Icon>
        </ModalBlock>
        <ModalContent
          onSubmit={(e: React.SyntheticEvent) =>
            !props?.isEditGroupModal && props?.onAddMembers
              ? props?.onAddMembers(e, selectedUsers)
              : props?.onEditGroupName && props?.onEditGroupName(e)
          }>
          <ModalForm>
            <Label>{props?.label}</Label>
            {props?.isEditGroupModal ? (
              <ModalInput
                type='text'
                placeholder='Enter group name'
                value={inputChatGroup?.name || ''}
                onChange={(e) => props?.onChange && props.onChange('name', e.target.value)}
              />
            ) : (
              <Dropdown
                isMinWidth={isMinWidth}
                content={
                  <InputDiv onClick={handleToggleDropdown}>
                    <InputData>
                      {selectedUsers.map((userId) => {
                        const user = filteredUsersList?.find((u) => u.id === userId);
                        if (!user) return null;
                        return (
                          <SelectedUser key={userId}>
                            {user.name || 'Unknown User'}
                            <span onClick={() => handleRemoveUser(userId)}>
                              <SVGIcon name='cross-icon' width='8px' height='8px' viewBox='0 0 10 10' />
                            </span>
                          </SelectedUser>
                        );
                      })}

                      <Input
                        type='text'
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={selectedUsers.length === 0 ? 'Enter member name' : ''}
                      />
                    </InputData>

                    <SVGIcon
                      name='angleDown-icon'
                      width='18'
                      height='18'
                      viewBox='0 0 18 18'
                      className={`angleDown-icon ${isDropdownOpen ? 'rotate' : ''}`}
                    />
                  </InputDiv>
                }
                trigger='click'>
                <Dropdiv ref={dropdownRef} tabIndex={0} onKeyDown={handleKeyDown}>
                  {filteredUsersList
                    ?.filter(
                      (user) =>
                        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
                    )
                    .map((user, index) => {
                      const isSelected = selectedUsers.includes(user.id);
                      return (
                        <div key={user.id} ref={(el) => (itemRefs.current[index] = el)}>
                          <DropdownItem
                            isChecked={isSelected}
                            label={user.name}
                            isAvatar={true}
                            avatarSrc={user.profile_photo || ''}
                            onClick={() => handleSelectChangeUsers(user.id)}
                            isActive={isSelected || index === currentIndex}
                          />
                        </div>
                      );
                    })}
                </Dropdiv>
              </Dropdown>
            )}
          </ModalForm>
          <ModalButton>
            <Button
              modelBtn={true}
              title='Cancel'
              secondary={true}
              onClick={props?.onClose}
              isCancelbtn={true}
              hasNotBoxShadow
            />
            {props.isEditGroupModal ? (
              <Button type={'submit'} modelBtn={true} title='Save' isLoading={props?.loading} />
            ) : (
              <Button type={'submit'} modelBtn={true} title='Add' isLoading={props?.loading} />
            )}
          </ModalButton>
        </ModalContent>
      </Main>
    </div>
  );
};

export default Addgroupmembermodal;
